import type LessonHistory from '@whizz/api-client/dist/@types/model/tx3/LessonHistory';
import { LessonOutcomeStatusKeyEnum, LessonStatusEnum } from '@whizz/api-model';

export type LessonRowStatus = LessonOutcomeStatusKeyEnum;

/**
 * Client-side fallback only - used for any lesson the API hasn't returned a real `outcomeStatus`
 * for (an environment/backend build that predates that field - see buildScaffoldAwareStatuses
 * below). Mirrors the backend's own rules as closely as possible from the fields we do have:
 * - RRG/Topic Challenge (Replay) lessons are binary Pass/Fail at 60%, no Static - confirmed via
 *   LessonResultService._getOutcomeLabel() (api repo)'s "Replay and assessment run modes will
 *   set outcome to zero" branch: `score >= 60 ? 'passed' : 'failed'`.
 * - Everything else uses the existing @whizz/api-model LessonHistory.getStatus() heuristic.
 */
const fallbackStatus = (lesson: LessonHistory): LessonOutcomeStatusKeyEnum => {
  if (lesson.getType() === 'Replay') {
    const incomplete = (lesson as unknown as { incomplete?: string | boolean }).incomplete;
    if (incomplete) return LessonOutcomeStatusKeyEnum.INCOMPLETE;

    const score = Number.parseInt(lesson.getMark(), 10);
    return score >= 60 ? LessonOutcomeStatusKeyEnum.PASSED : LessonOutcomeStatusKeyEnum.FAILED;
  }

  switch (lesson.getStatus()) {
    case LessonStatusEnum.INCOMPLETE: return LessonOutcomeStatusKeyEnum.INCOMPLETE;
    case LessonStatusEnum.JUMPED: return LessonOutcomeStatusKeyEnum.JUMPED;
    case LessonStatusEnum.REGRESSED: return LessonOutcomeStatusKeyEnum.FAILED;
    case LessonStatusEnum.STATIC: return LessonOutcomeStatusKeyEnum.STATIC;
    default: return LessonOutcomeStatusKeyEnum.PASSED;
  }
};

/**
 * Fallback-only approximation of the backend's regression/Scaffold chain, for lessons with no
 * real `outcomeStatus`. If a learner fails an Exercise/Test (< 30%), the next Exercise/Test in
 * the SAME topic is treated as the regression retry and labelled "Scaffold" - no matter whether
 * it then Jumps, Passes, is Static, or Fails. An Incomplete retry doesn't resolve the pending
 * regression (the learner didn't really attempt it), so it carries over to their next real
 * attempt. Replay and Assessment lessons never start or consume a regression - RRG runs in its
 * own topic-challenge stack, and Assessments are diagnostic (backend's
 * LessonStatusLogicService.isRegression/causesRegression never include an ASSESSMENT_* code).
 */
const buildFallbackStatuses = (history: LessonHistory[]): Map<LessonHistory, LessonOutcomeStatusKeyEnum> => {
  const statuses = new Map<LessonHistory, LessonOutcomeStatusKeyEnum>();
  const failedTopics = new Set<string>();

  [ ...history ]
    .sort((a, b) => a.getTimestamp().toMillis() - b.getTimestamp().toMillis())
    .forEach((lesson) => {
      const status = fallbackStatus(lesson);
      const type = lesson.getType();

      if (type === 'Replay' || type === 'Assessment') {
        statuses.set(lesson, status);
        return;
      }

      const topic = lesson.getTopic();
      const isPendingRegression = failedTopics.has(topic);

      if (isPendingRegression && status === LessonOutcomeStatusKeyEnum.INCOMPLETE) {
        statuses.set(lesson, status);
        return;
      }

      if (isPendingRegression) {
        statuses.set(lesson, LessonOutcomeStatusKeyEnum.SCAFFOLD);
        failedTopics.delete(topic);
        return;
      }

      statuses.set(lesson, status);
      if (status === LessonOutcomeStatusKeyEnum.FAILED) failedTopics.add(topic);
    });

  return statuses;
};

/**
 * Source of truth: the backend now computes the real, regression-aware outcome status
 * (Passed/Failed/Static/Scaffold/Incomplete/Jumped) server-side from the actual ingestion
 * status code and returns it as `outcomeStatus` on the lesson-history response (see
 * LessonStatusLogicService.getLessonOutcomeStatus in the api repo, wired into
 * LessonHistoryReportService.mapResponse()). We use that directly whenever it's present.
 *
 * Only lessons missing it fall back to the client-side approximation above - this should only
 * happen against a backend build that predates this field (e.g. a local devenv not yet rebuilt
 * from the `uat` api/api-model branches). If a whole page of history has no `outcomeStatus` at
 * all, the fallback runs with full topic-chain context; if only a few scattered lessons are
 * missing it (unexpected in practice), each just gets its own simple fallback status without
 * chain context, rather than guessing at a chain the real data already answers for its peers.
 */
export const buildScaffoldAwareStatuses = (history: LessonHistory[]): Map<LessonHistory, LessonRowStatus> => {
  const statuses = new Map<LessonHistory, LessonRowStatus>();
  const missingOutcomeStatus: LessonHistory[] = [];

  history.forEach((lesson) => {
    const outcomeStatus = lesson.getOutcomeStatus();
    if (outcomeStatus) {
      statuses.set(lesson, outcomeStatus);
    } else {
      missingOutcomeStatus.push(lesson);
    }
  });

  if (missingOutcomeStatus.length === 0) return statuses;

  const fallback = missingOutcomeStatus.length === history.length
    ? buildFallbackStatuses(history)
    : new Map(missingOutcomeStatus.map((lesson) => [ lesson, fallbackStatus(lesson) ]));

  fallback.forEach((status, lesson) => statuses.set(lesson, status));

  return statuses;
};
